import React, { useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { FlexContainer } from '@app/common/components/FlexContainer/FlexContainer';
import { FieldComponent, FieldLocator } from '@app/common/configurable-wizards';
import { PasswordFieldFragment } from '@app/common/configurable-wizards/queries/queryTyping/all-fields';
import { combineNames, isPasswordField } from '@app/common/configurable-wizards/utils';

import { useGetSecuritySettingsQuery } from '../../queries/queryTyping/getSecuritySettings';

import { CompromisedCredentials } from './CompromisedCredentials';
import { PasswordConfirmationInput } from './PasswordConfirmationInput';
import { PasswordInput } from './PasswordInput';
import { PasswordPolicies } from './PasswordPolicies';
import { PasswordStrength } from './PasswordStrength';

const PASSWORD_DESCRIPTION_ID = 'new-password-description';

export const NewPasswordFieldComponent: FieldComponent<PasswordFieldFragment> = ({
  parent,
  field,
}) => {
  const { watch } = useFormContext();
  const [isPasswordShown, setPasswordShown] = useState(false);

  const { data: securitySettings } = useGetSecuritySettingsQuery({
    fetchPolicy: 'cache-and-network',
  });

  // Commented.
  // Because was decided to postpone related functionality
  // and release it again with some possible improvements (todo in MPH-40983)
  // const [inputFocused, setInputFocused] = useState(false);
  const onPasswordShownToggle = useCallback(
    (value) => setPasswordShown(value),
    [],
  );

  const fieldName = combineNames(parent, field.id);
  const currentValue = watch(fieldName);

  return (
    <FlexContainer direction="column">
      <div>
        {/* FIELD SECTION  */}
        <PasswordInput
          field={field}
          fieldName={fieldName}
          aria-describedby={PASSWORD_DESCRIPTION_ID}
          // Commented.
          // Because was decided to postpone related functionality
          // and release it again with some possible improvements (todo in MPH-40983)
          // onFocusChange={setInputFocused}
          onPasswordShownToggle={(onPasswordShownToggle)}
        />

        {/* POLICIES SECTION  */}
        {field.passwordPolicy && (
          <div id={PASSWORD_DESCRIPTION_ID}>
            <PasswordPolicies
              policySettings={field.passwordPolicy}
              // Commented.
              // Because was decided to postpone related functionality
              // and release it again with some possible improvements (todo in MPH-40983)
              // relatedInputFocused={inputFocused}
              relatedInputFocused
              fieldName={fieldName}
            />
          </div>
        )}
      </div>

      <div>
        {/* PASSWORD STRENGTH SECTION */}
        {field.passwordPolicy?.passwordStrengthMeterEnabled && (
          <PasswordStrength
            policySettings={field.passwordPolicy}
            passwordValue={currentValue}
          />
        )}

        {/* COMPROMISED CREDENTIALS CHECK SECTION  */}
        {securitySettings?.securitySettings.compromisedCredentialsCheckEnabled
        && <CompromisedCredentials password={currentValue} />}

        {/* CONFIRM FIELD SECTION  */}
        {!isPasswordShown && (
          <PasswordConfirmationInput
            field={field}
            passwordFieldName={fieldName}
          />
        )}
      </div>
    </FlexContainer>
  );
};

export const NewPasswordFieldLocator: FieldLocator = ({ field }) => (
  (isPasswordField(field))
    ? NewPasswordFieldComponent
    : undefined
);
